/**
 * Numbers that carry a decision. Anything self-evident lives at its use site.
 */

/**
 * 20 minutes. This deliberately breaks the house rule, and the reason matters.
 *
 * Every other generation in this app is sized UNDER the client's patience —
 * `suggest_follow_ups_task` and `generate_scribe_template_task` are both
 * `soft_time_limit=150` BECAUSE the web app gives up at 180s. Sales Coach inverts
 * that: the task reads a whole visit transcript three times at 900s soft / 960s
 * hard, and the poll's own stale sweep only declares a silent run dead after 1200s.
 *
 * So the CLIENT budget moves instead. Giving up at 180s shows a failure on a run
 * that is alive and will succeed, and the row then reads `succeeded` on the next
 * page load with nothing having told the user. This is a FLOOR, not a target — a
 * typical run is 2–4 minutes.
 *
 * Do NOT reuse `GENERATION_TIMEOUT_MS` from `follow-ups.constants.ts` (180_000).
 * It is a different contract for a different task.
 */
export const SALES_REVIEW_TIMEOUT_MS = 1_200_000;

export const SALES_REVIEW_POLL_MS = 3_000;

/**
 * How long to wait before the single verification read after a 202 that came back
 * `not_analysable`. The server either re-armed the row or refused it (the recording
 * gate's answer cannot have changed), and the response does not say which.
 */
export const ANALYSE_VERIFY_DELAY_MS = 2_500;

/** Lines of transcript shown around an evidence anchor, centred on it. */
export const EVIDENCE_WINDOW_LINES = 20;

/** The query param that opens the review modal. A review id, always a number. */
export const REVIEW_PARAM = "review";

/**
 * The query param that scrolls the Playbook to one play. A play id, e.g.
 * `D2.companion_direct_ask` — free-form text, so always encode it.
 *
 * Unlike `REVIEW_PARAM` this is a LOCATION rather than a command, so the page does
 * not strip it after acting: refresh and re-share have to keep working.
 */
export const PLAY_PARAM = "play";

/**
 * The query param that scrolls the Playbook to one BEHAVIOUR, e.g. `clinical_workup`.
 *
 * A location like `PLAY_PARAM`, not a command: the page scrolls, flashes nothing away,
 * and leaves the param in the URL so refresh and re-share keep working.
 */
export const DIMENSION_PARAM = "dimension";

/** Page/sort state on Opportunities, so a refresh or a shared link lands where it was. */
export const PAGE_PARAM = "page";
export const ORDERING_PARAM = "sort";

/** Reading measure. Long lines cost reading speed more than small type does. */
export const PROSE_MAX_WIDTH = "68ch";

/**
 * Content measure for the section's non-Overview pages.
 *
 * They were written against a pane that was accidentally 1009px wide (see the comment in
 * `layout/sales-coach-layout.component.tsx`). Widened to 1820px without a cap they look
 * broken rather than roomy — a Playbook card becomes 1,740px of chrome around 68ch of text.
 *
 * ⚠️ **Only the Playbook uses this now.** Three of the four pages opt out, for three
 * different reasons — so do not reach for it on a new page without deciding which case the
 * new page is. The rule they share: this constant protects PROSE that has no measure of its
 * own, and where prose is already capped per element it only starves the layout.
 *
 *   - The **Overview** wants more: its two-column grid is the reason the width fix exists.
 *   - **Training** wants less, and sets its own measure. Capping it here only relocated the
 *     problem: with a 280px rail beside it, a drill card came out ~1,140px wrapping 68ch of
 *     prose — the same card-adrift-in-white-space this constant exists to prevent. It is
 *     the section's one reading-and-doing surface, so it caps near its own measure and
 *     centres in the pane, the way a study app does.
 *   - **Opportunities** wants all of it: its card holds a nine-column table rather than a
 *     paragraph, and every prose element on the page already carries `PROSE_MAX_WIDTH`
 *     itself. The cap was costing the table ~380px and buying nothing.
 */
export const SECTION_CONTENT_MAX_WIDTH = 1440;

/** Minimum interactive target. A 24px icon is not a click target. */
export const MIN_TARGET = 44;

/**
 * How long a jumped-to play or move stays tinted after the scroll lands.
 *
 * Long enough to be seen after a smooth scroll settles, short enough that it reads as
 * "you arrived here" rather than "this row is selected". It is a background flash and
 * never motion, so it survives `prefers-reduced-motion` intact.
 */
export const ANCHOR_HIGHLIGHT_MS = 1_600;

/**
 * The v2 catalog version (SN-19967). Mirrors the backend's `V2_FRAMEWORK_VERSION`
 * (`coach/services/sales_playbook.py`). v2 is used only while `/coach/playbook/` lists it in
 * `available_versions`, which the backend stops doing when `SALES_COACH_V2_ENABLED` is off.
 */
export const SALES_COACH_V2_FRAMEWORK_VERSION = "2026-10-19";
